import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { BriefcaseIcon, SlidersHorizontal } from 'lucide-react';
import { formatCurrencyAmount } from '../../../../../utils/miscFunctions';
import SelectDropdown from '../../../../../components/form-control/SelectDropdown';


interface IBranch {
  branchId: number;
branchName: string;
growthRate: number;
policyCount: number;
previousPeriodPolicyCount: number;
previousPeriodRevenue: number;
revenue: number;
revenueTarget: any;
targetAchievementPercentage: any;
volumeGrowthRate: number;
}
 interface InsuranceBranchPerformance {
 bestPerformingBranch: string;
branchMetrics: IBranch[];
companyRevenueTarget: number;
companyTargetAchievementPercentage?: number;
slowestGrowthBranch?: string;
totalRevenue: number;
}
interface InsuranceBranchPerformanceProps {
  data: InsuranceBranchPerformance;
}

type MetricType = 'growthRate' | 'policyCount' | 'revenue';

const BranchPerformance: React.FC<InsuranceBranchPerformance> = (
  { 
     bestPerformingBranch,
branchMetrics,
companyRevenueTarget,
companyTargetAchievementPercentage,
slowestGrowthBranch,
totalRevenue
}
) => {
  const [metricType, setMetricType] = useState<MetricType>('policyCount');

  const getMetricLabel = () => {
    switch (metricType) {
      case 'growthRate': return 'Growth Rate';
      case 'policyCount': return 'Policy Count';
      case 'revenue': return 'Revenue';
    }
  };

  // Define colors for the chart
  const colors = [
    '#c7b8ea', // indigo-100
    '#34d399', // emerald-100
    '#feb546', // amber-100
    '#f87171', // red-100
    '#c084fc', // violet-100
    '#f9a8d4', // pink-100
  ];

  // Transform data for the chart
  const chartData =  branchMetrics?.map((branch, index) => ({
    name: branch.branchName,
    revenue: branch.revenue || 0,
    growthRate: branch.growthRate || 0,
    policyCount: branch.policyCount || 0,
    color: colors[index % colors.length], // Assign color based on index
  }));

const CustomLegend = (value:any) => (
  <div className='flex items-center justify-center w-full'>
    <span className='w-[12px] h-[12px] bg-black dark:bg-white mr-2' />
    <span className='text-black dark:text-white' >
      {value}
    </span>
  </div>
);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 dark:bg-darkBg dark:border dark:border-darkBg-border">
      <div className="flex justify-between items-center mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Branch Performance</h3>
          {/* <p className="text-sm text-gray-600">policyCount distribution across insurance providers</p> */}
        </div>

        <div className="">
          <SelectDropdown
            list={[
              { value: 'policyCount', name: 'Policy Count' },
              { value: 'growthRate', name: 'Growth Rate' },
              { value: 'revenue', name: 'Revenue' },
            ]}
            listSelectedValue={metricType}
            onChange={(e: string) => setMetricType(e as MetricType)}
            wrapperClasses='w-full min-w-[150px]'
          />
        </div>
      </div>

      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 50, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />
            <XAxis
              type="number"
              tickFormatter={(value: number) => metricType === 'policyCount' ||  metricType === 'growthRate' ? `${value}` : formatCurrencyAmount(value)}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              type="category"
              dataKey="name"
              tick={{ fontSize: 12 }}
              width={80}
            />
            <Tooltip
              formatter={((value: number) => metricType === 'policyCount' ||  metricType === 'growthRate'  ? `${value}` : formatCurrencyAmount(value))}
              labelStyle={{ fontWeight: 'bold' }}
            />
            <Legend content={CustomLegend(getMetricLabel())} />
            <Bar
              dataKey={metricType}
              name={getMetricLabel()}
              radius={[0, 4, 4, 0]}
              barSize={22}
            
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="bg-gray-50 p-3 rounded-2xl dark:bg-darkBg dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-darkBg-mainText">Total Revenue</div>
            <BriefcaseIcon size={16} className="text-blue-600" />
          </div>
          <p className="text-lg font-bold text-gray-900 mt-1 dark:text-darkBg-mainText">
            {totalRevenue}
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-2xl dark:bg-darkBg dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-darkBg-mainText">Revenue Target</div>
            <BriefcaseIcon size={16} className="text-green-600" />
          </div>
          <p className="text-lg font-bold text-gray-900 mt-1 dark:text-darkBg-mainText">
            {companyRevenueTarget}
          </p>
        </div>
        <div className="bg-gray-50 p-3 rounded-2xl dark:bg-darkBg dark:border dark:border-darkBg-border">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-darkBg-mainText">Best Performing Branch</div>
            <BriefcaseIcon size={16} className="text-yellow-600" />
          </div>
          <p className="text-lg font-bold text-gray-900 mt-1 dark:text-darkBg-mainText">
            {bestPerformingBranch ? bestPerformingBranch : '-'}
          </p>
        </div>
      </div>

    </div>
  );
};

export default BranchPerformance;
